import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export class Accordion {
  constructor() {
    this.isInitialized = false;
    this.accordions = new Map();
  }

  init() {
    if (this.isInitialized) return;

    this.initAccordions();
    this.isInitialized = true;
  }

  initAccordions() {
    document.querySelectorAll('.accordion_wrap').forEach((container, index) => {
      if (this.accordions.has(container)) {
        this.destroyAccordion(container);
      }

      const accordion = new AccordionInstance(container, index);
      this.accordions.set(container, accordion);
    });
  }

  destroyAccordion(container) {
    const accordion = this.accordions.get(container);
    if (accordion) {
      accordion.destroy();
      this.accordions.delete(container);
    }
  }

  destroy() {
    this.accordions.forEach((accordion, container) => {
      this.destroyAccordion(container);
    });

    this.isInitialized = false;
  }
}

class AccordionInstance {
  constructor(container, index) {
    this.container = container;
    this.index = index;
    this.timelines = [];
    this.closeFunctions = [];
    this.previousIndex = null;

    // Get configuration from data attributes
    this.closePrevious = container.getAttribute('data-close-previous') !== 'false';
    this.closeOnSecondClick = container.getAttribute('data-close-on-second-click') !== 'false';
    this.openOnHover = container.getAttribute('data-open-on-hover') === 'true';

    const openByDefaultAttr = container.getAttribute('data-open-by-default');
    this.openByDefault = openByDefaultAttr !== null && !isNaN(+openByDefaultAttr)
      ? +openByDefaultAttr
      : false;

    this.list = container.querySelector('.accordion_list');

    this.init();
  }

  init() {
    this.removeCMSList();
    this.setupAccordionItems();
  }

  removeCMSList() {
    if (!this.list) return;

    const dynList = Array.from(this.list.children).find((child) =>
      child.classList.contains('w-dyn-list')
    );

    if (!dynList) return;

    const nestedItems = dynList?.firstElementChild?.children;
    if (!nestedItems) return;

    const staticWrapper = [...this.list.children];
    [...nestedItems].forEach(el => {
      if (el.firstElementChild) {
        this.list.appendChild(el.firstElementChild);
      }
    });

    staticWrapper.forEach((el) => el.remove());
  }

  setupAccordionItems() {
    this.container.querySelectorAll('.accordion_component').forEach((card, cardIndex) => {
      card.style.setProperty('--_state---false', '0');
      const button = card.querySelector('.accordion_toggle_button');
      const content = card.querySelector('.accordion_content_wrap');

      if (!button || !content) {
        console.warn('Missing elements:', card);
        return;
      }

      this.setupAccordionItem(card, button, content, cardIndex);
    });
  }

  setupAccordionItem(card, button, content, cardIndex) {
    // Set ARIA attributes
    const buttonId = `accordion_button_${this.index}_${cardIndex}`;
    const contentId = `accordion_content_${this.index}_${cardIndex}`;

    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('id', buttonId);
    button.setAttribute('aria-controls', contentId);

    content.setAttribute('id', contentId);
    content.setAttribute('aria-labelledby', buttonId);
    content.style.display = 'none';

    // Create animation timeline
    const refresh = () => {
      tl.invalidate();
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    };

    const tl = gsap.timeline({
      paused: true,
      defaults: {
        duration: 0.3,
        ease: 'power1.inOut'
      },
      onComplete: refresh,
      onReverseComplete: refresh
    });

    tl.set(content, { display: 'block' });
    tl.fromTo(content, { height: 0 }, { height: 'auto' });

    // Store timeline for cleanup
    this.timelines.push(tl);

    // Close function
    const closeAccordion = () => {
      if (card.classList.contains('is-active')) {
        card.classList.remove('is-active');
        tl.reverse();
        button.setAttribute('aria-expanded', 'false');
        card.style.setProperty('--_state---false', '0');
      }
    };

    this.closeFunctions[cardIndex] = closeAccordion;

    // Open function
    const openAccordion = (instant = false) => {
      if (this.closePrevious && this.previousIndex !== null && this.previousIndex !== cardIndex) {
        this.closeFunctions[this.previousIndex]?.();
      }

      this.previousIndex = cardIndex;
      button.setAttribute('aria-expanded', 'true');
      card.classList.add('is-active');
      card.style.setProperty('--_state---false', '1');

      if (instant) {
        tl.progress(1);
      } else {
        tl.play();
      }
    };

    // Open by default
    if (this.openByDefault === cardIndex + 1) {
      openAccordion(true);
    }

    // Event listeners
    button.addEventListener('click', () => {
      // Trigger coins jump - dispatch custom event
      window.dispatchEvent(new CustomEvent('accordion:toggle', {
        detail: { force: 15 }
      }));

      if (card.classList.contains('is-active') && this.closeOnSecondClick) {
        closeAccordion();
        this.previousIndex = null;
      } else {
        openAccordion();
      }
    });

    if (this.openOnHover) {
      button.addEventListener('mouseenter', () => openAccordion());
    }
  }

  destroy() {
    // Kill all timelines
    this.timelines.forEach(tl => {
      if (tl) {
        tl.kill();
      }
    });
    this.timelines = [];

    // Clear references
    this.closeFunctions = [];
  }
}